import yup from "yup";
import { action, computed, observable, toJS } from "mobx";
import { FormStore } from "@modules/formx";
import { store } from "@store";
import locale from "@bonliva-crm/locale";
import { TodoPriority, UserFragment } from "@graphql";
import { Props } from "./CommentBox";
import { TodoTags } from "@bonliva-crm/common";
import { ReminderFormData } from "@containers/ReminderDialog/ReminderStore";
import { OkCancelDialogOptions } from "@containers/ConfirmDialog";

export type CommentBoxData = {
  text: string;
  remindOn?: Date;
  assignTo?: UserFragment;
  assignTeam?: string;
  tags?: string[];
  priority?: TodoPriority;
};

const initialValues = {
  text: "",
  remindOn: null,
  assignTeam: null,
  assignTo: null as UserFragment,
  tags: [],
  priority: null
};

export class CommentBoxStore extends FormStore<CommentBoxData> {
  @observable dropdownBtnIndex = 0;
  @observable showDatePicker = false;
  @observable tags = [];

  @computed get isReminderDialogOpen() {
    return this._isReminderDialogOpen;
  }

  @computed get reminderDialogInitialValues() {
    return this._reminderDialogInitialValues;
  }

  @observable private _reminderDialogInitialValues: ReminderFormData | null = null;
  @observable private _isReminderDialogOpen = false;
  private _onReminderDialogComplete: (v: ReminderFormData) => Promise<any>;

  constructor(public readonly props: Props<any>) {
    super({
      schema: yup.object({
        text: yup.string().required("Message is required"),
        remindOn: yup.date().nullable(true),
        assignTo: yup.mixed(),
        assignTeam: yup.string().nullable(true),
        priority: yup.mixed().nullable(true),
        tags: yup.array()
      }),
      debounceValidationMs: 250
    });

    this.tags = Object.values(TodoTags).map((x) => ({
      label: x,
      value: x
    }));

    this.reset(initialValues);
  }

  @action.bound onComment() {
    const { onComment, skipReminder } = this.props;

    if (!onComment) {
      return;
    }

    this.setSubmitting(true);
    if (skipReminder) {
      onComment(toJS(this.values), null, this)
        .then(() => {
          this.reset(initialValues);
        })
        .finally(() => this.setSubmitting(false));
    } else {
      this.openReminderDialog(this.getInitialReminderValues(), (reminder) =>
        onComment(toJS(this.values), reminder, this)
          .then(() => {
            this.reset(initialValues);
            reminder && this.showReminderCreatedSnack();
          })
          .finally(() => this.setSubmitting(false))
      );
    }
  }

  @action.bound onInterest() {
    const { onInterest } = this.props;

    if (!onInterest) {
      return;
    }

    store()
      .ui.confirmDialog.confirm(
        new OkCancelDialogOptions(locale.interestEventConfirmation)
      )
      .then((res) => {
        if (res === "ok") {
          this.setSubmitting(true);
          onInterest(toJS(this.values))
            .then(() => {
              this.reset(initialValues);
            })
            .finally(() => this.setSubmitting(false));
        }
      });
  }

  @action.bound onNoAnswer() {
    const { onNoAnswer } = this.props;

    if (!onNoAnswer) {
      return;
    }

    onNoAnswer(toJS(this.values))
      .then(() => {
        this.reset(initialValues);
      })
      .finally(() => this.setSubmitting(false));
  }

  @action.bound onMetInPerson() {
    const { onMetInPerson, skipReminder } = this.props;

    if (!onMetInPerson) {
      return;
    }

    if (skipReminder) {
      onMetInPerson(toJS(this.values), null)
        .then(() => {
          this.reset(initialValues);
        })
        .finally(() => this.setSubmitting(false));
      return;
    }

    this.openReminderDialog(
      { ...this.getInitialReminderValues(), date: this.values.remindOn },
      (reminder) =>
        onMetInPerson(toJS(this.values), reminder)
          .then(() => {
            this.reset(initialValues);
            reminder && this.showReminderCreatedSnack();
          })
          .finally(() => this.setSubmitting(false))
    );
  }

  @action.bound onReminder() {
    const { onReminder } = this.props;

    if (!onReminder) {
      return;
    }

    onReminder(toJS(this.values))
      .then(() => {
        this.reset(initialValues);
      })
      .finally(() => this.setSubmitting(false));
  }

  @action.bound onChat() {
    const { onChat } = this.props;

    if (!onChat) {
      return;
    }

    onChat(toJS(this.values))
      .then(() => {
        this.reset(initialValues);
      })
      .finally(() => this.setSubmitting(false));
  }

  @action.bound onEmail() {
    const { onEmail } = this.props;

    if (!onEmail) {
      return;
    }

    onEmail()
      .then(() => {
        this.reset(initialValues);
      })
      .finally(() => this.setSubmitting(false));
  }

  @action.bound onSMS() {
    const { onSMS } = this.props;

    if (!onSMS) {
      return;
    }

    onSMS(toJS(this.values))
      .then(() => {
        this.reset(initialValues);
      })
      .finally(() => this.setSubmitting(false));
  }

  @action.bound openReminderDialog(
    initialValues: ReminderFormData,
    onComplete: (values: ReminderFormData) => Promise<any>
  ) {
    this._reminderDialogInitialValues = initialValues;
    this._isReminderDialogOpen = true;
    this._onReminderDialogComplete = onComplete;
  }

  @action.bound cancelReminderDialog() {
    this.setSubmitting(false);
    this.closeReminderDialog();
  }

  @action.bound submitReminderDialog(reminderData: ReminderFormData) {
    if (!this._onReminderDialogComplete) {
      throw new Error("missing onComplete callback");
    }

    this.closeReminderDialog();
    return this._onReminderDialogComplete(reminderData);
  }

  @action.bound skipReminderDialog() {
    if (!this._onReminderDialogComplete) {
      throw new Error("missing onComplete callback");
    }

    this.closeReminderDialog();
    return this._onReminderDialogComplete(null);
  }

  @action.bound private closeReminderDialog() {
    this._reminderDialogInitialValues = null;
    this._isReminderDialogOpen = false;
  }

  get priorityOptions() {
    return [
      { label: "Ingen prioritet", value: null },
      { label: "Låg prioritet", value: "Low" },
      { label: "Hög prioritet", value: "High" }
    ];
  }

  private getInitialReminderValues = () => ({
    date: null,
    text: this.values.text
  });

  private showReminderCreatedSnack = () => {
    store().snackbars.success(locale.ReminderCreated);
  };
}
